import React, { useState } from 'react';
import Loader, { CommonLoader } from '../Common/Loader';
import Paper from '@material-ui/core/Paper';
import Avatar from '@material-ui/core/Avatar';
import { Row, Col, Input } from 'reactstrap';
import CommonTooltip from '../Common/CommonTooltip';
import moment from 'moment';
import { FormGroup, MenuItem, Select, Button } from '@material-ui/core';
import { formResMessage, convertModuleToName } from 'utils/commonFunc';
import PeopleIcon from '@material-ui/icons/People';
import EventIcon from '@material-ui/icons/Event';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import BadgeIcon from '@material-ui/icons/Person';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import AssignmentIcon from '@material-ui/icons/Assignment';
import VideocamIcon from '@material-ui/icons/Videocam';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import HistoryIcon from '@material-ui/icons/History';
import colors from 'config/colors';

// Icon + color per activity module, so the feed reads at a glance instead of being a
// flat wall of text. Falls back to a neutral "history" badge for anything unmapped.
const MODULE_STYLE = {
  STUDENT_MODULE: { icon: PeopleIcon, color: '#397af8' },
  BATCH_MODULE: { icon: EventIcon, color: colors.accent },
  COURSE_MODULE: { icon: MenuBookIcon, color: '#00897b' },
  EMPLOYEE_MODULE: { icon: BadgeIcon, color: '#f9a825' },
  CIRCULAR_MODULE: { icon: NotificationsActiveIcon, color: '#e53935' },
  ASSIGNMENT_MODULE: { icon: AssignmentIcon, color: '#5e35b1' },
  ONLINE_CLASS_MODULE: { icon: VideocamIcon, color: '#43a047' },
  EXAM_MODULE: { icon: AssignmentTurnedInIcon, color: '#fb8c00' },
  EXTRA_CURRICULAR_MODULE: { icon: EmojiEventsIcon, color: '#d81b60' },
};

const getModuleStyle = moduleName => MODULE_STYLE[moduleName] || { icon: HistoryIcon, color: '#8b95a1' };

const ActivityList = ({
  recentActivitiesFetching,
  activities,
  limit,
  handleLimitChange,
  showLimitAlways = false,
  sendVoice,
  updateSuccess,
  showWarning,
}) => {
  const [auditDescription, setAudit] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = e => {
    e.preventDefault();
    if (auditDescription) {
      setLoading(true);
      sendVoice({ auditDescription })
        .then(() => {
          setLoading(false);
          setAudit('');
          updateSuccess?.();
        })
        .catch(err => {
          setLoading(false);
          showWarning(formResMessage(err));
        });
    }
  };

  const noDataDiv = (
    <div style={{ textAlign: 'center', paddingTop: 40, paddingBottom: 40, color: '#8b95a1' }}>
      <HistoryIcon style={{ fontSize: 36, opacity: 0.4 }} />
      <div style={{ marginTop: 8 }}>No activities found</div>
    </div>
  );

  return (
    <div>
      {recentActivitiesFetching ? (
        <div style={{ textAlign: 'center' }}>
          <CommonLoader />
        </div>
      ) : (
        <>
          {(showLimitAlways || activities?.length > 0) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 6,
                marginBottom: 8,
              }}>
              <span style={{ color: '#8b95a1', fontSize: 12 }}>Show</span>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={limit}
                style={{ minWidth: 60, fontSize: 13 }}
                onChange={handleLimitChange}>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={200}>200</MenuItem>
              </Select>
            </div>
          )}
          {sendVoice && (
            <form onSubmit={handleSubmit}>
              <Paper
                variant="outlined"
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', marginBottom: 12 }}>
                <Input
                  style={{ border: 'none', boxShadow: 'none', flex: 1 }}
                  type="text"
                  name="auditDescription"
                  onChange={e => setAudit(e.target.value)}
                  value={auditDescription}
                  placeholder="Announce something to everyone..."
                />
                {auditDescription && (
                  <Button size="small" type="submit" color="secondary" variant="contained" disabled={loading}>
                    {loading ? <Loader size={20} /> : 'Add'}
                  </Button>
                )}
              </Paper>
            </form>
          )}
          {activities?.length > 0
            ? activities?.map((el, k) => {
                const { icon: ModuleIcon, color } = getModuleStyle(el?.moduleName);
                return (
                  <Paper key={k} variant="outlined" style={{ padding: 12, marginBottom: 8, borderRadius: 8 }}>
                    <Row style={{ margin: 0 }}>
                      <Avatar style={{ backgroundColor: `${color}1a`, color, marginRight: 12, width: 36, height: 36 }}>
                        <ModuleIcon fontSize="small" />
                      </Avatar>
                      <Col style={{ padding: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ fontSize: 14 }}>
                            {el?.auditDescription}{' '}
                            {el?.changesFor && (
                              <>
                                for <span style={{ color: '#6b778a', fontWeight: 'bold' }}>{el?.changesFor}</span>
                              </>
                            )}
                          </div>
                          <CommonTooltip content={moment(el.creationDateTime).format('DD MMM, YYYY hh:mm A')}>
                            <span style={{ color: '#8b95a1', fontSize: 12, whiteSpace: 'nowrap', marginLeft: 10 }}>
                              {moment(el.creationDateTime).fromNow()}
                            </span>
                          </CommonTooltip>
                        </div>
                        <div style={{ color: '#8b95a1', fontSize: 12, marginTop: 4 }}>
                          {el?.moduleName && <span>{convertModuleToName(el.moduleName)} · </span>}
                          Updated by{' '}
                          <CommonTooltip content={el?.fromUserName}>
                            <span>{el?.fromUserFullName}</span>
                          </CommonTooltip>
                        </div>
                      </Col>
                    </Row>
                  </Paper>
                );
              })
            : noDataDiv}
        </>
      )}
    </div>
  );
};

export default ActivityList;
